import { defineStore } from 'pinia';
import { axiosInfraInstance, axiosOrgInstance, makeRequest, type ApiEnvelope } from './middleware';
import {
    type NotificationType,
    type UUIDLocation,
    type NotificationTemplate,
    type NotificationResponseById,
    type AllNotificationResponse,
    type Toast,
    type ToastType,
    type NotificationFormData,
    type NotificationSubscriptionResponse,
    type SubscriptionFormBody,
    type OrganizationByLocation,
    type NotificationThreadResponseById,
    NOTIFICATION_SEVERITY,
} from './store-interfaces';
import { computed, ref } from 'vue';
import { useOrganizationStore } from './organization';
import { sortLocationTree } from '@/lib/location-order';

export const useNotificationStore = defineStore('notification', () => {
    const subscriptions = ref<AllNotificationResponse[]>([]);
    const orgNotifications = ref<AllNotificationResponse[]>([]);
    const toastMessage = ref<Toast[]>([]);
    const hasSubscribed = ref<boolean>(false);

    const usersOrganizations = computed(() => useOrganizationStore().user?.organizations);
    const allOrganizations = computed(() => useOrganizationStore().availableOrganizations);
    const userOrganizationId = computed(() => useOrganizationStore().usersOrganization?.organization_id);

    const filteredNotificationOrganizations = computed(() =>
        usersOrganizations.value?.filter(
            (org) =>
                // check if user has notification access
                org.organization_roles.some((role) => ['org-admin', 'notification-portal'].includes(role.name)) &&
                allOrganizations.value
                    ?.find((allOrg) => allOrg.organization_id === org.organization_id)
                    ?.organization_roles.some((role) => ['notification-portal'].includes(role.name)),
        ),
    );

    const hasNotificationAccess = computed(
        () =>
            filteredNotificationOrganizations.value &&
            filteredNotificationOrganizations?.value?.length > 0 &&
            filteredNotificationOrganizations.value?.some((org) => org.organization_id === userOrganizationId.value),
    );

    async function setup(newOrgId?: number) {
        if (
            !hasNotificationAccess.value &&
            filteredNotificationOrganizations.value?.some((org) => org.organization_id === userOrganizationId.value)
        ) {
            return;
        }
        const usableOrgId = newOrgId ?? userOrganizationId.value;

        if (usableOrgId) {
            hasSubscribed.value = !!(await getSubscriptions().then((res) => {
                return res.find((sub) => sub.email_severity !== null);
            }));
            await getNotifications(usableOrgId, 'subscription').then((res) => {
                subscriptions.value = res;
            });

            await getNotifications(usableOrgId, 'organization').then((res) => {
                orgNotifications.value = res.filter(
                    (notification) => notification.severity === NOTIFICATION_SEVERITY.CRITICAL,
                );
            });
        } else {
            setTimeout(() => setup(), 1000);
        }
    }

    function setToastMessage(message: string, type: ToastType) {
        const index = toastMessage.value.length;
        toastMessage.value?.push({ message, type, index });
    }

    function clearToastMessage() {
        toastMessage.value.shift();
    }

    async function getAllNotifications() {
        // need to get this from organization api aswell
        const response = await makeRequest<ApiEnvelope<AllNotificationResponse[]>>(
            'GET',
            `admin-management/notifications`,
            axiosOrgInstance,
        );

        return response.data.data;
    }

    async function getNotificationTypes() {
        const response = await makeRequest<ApiEnvelope<NotificationType[]>>(
            'GET',
            `admin-management/notifications/types`,
            axiosOrgInstance,
        );

        return response.data.data;
    }

    async function getNotificationById(orgId: number, notificationId: string) {
        const response = await makeRequest<ApiEnvelope<NotificationResponseById>>(
            'GET',
            `organization-management/organizations/${
                orgId ?? localStorage.getItem('organization_id')
            }/notifications/${notificationId}`,
            axiosOrgInstance,
        );

        return response.data.data;
    }

    async function getAdminNotification(notificationId: string) {
        const response = await makeRequest<ApiEnvelope<NotificationResponseById>>(
            'GET',
            `admin-management/notifications/${notificationId}`,
            axiosOrgInstance,
        );

        return response.data.data;
    }

    async function getNotifications(orgId: number, scope: 'subscription' | 'organization' = 'subscription') {
        if (orgId === undefined) return [];
        const response = await makeRequest<ApiEnvelope<AllNotificationResponse[]>>(
            'GET',
            `organization-management/organizations/${orgId}/notifications?scope=${scope}`,
            axiosOrgInstance,
        );

        return response.data.data;
    }

    async function createNotification(body: NotificationFormData) {
        const response = await makeRequest<ApiEnvelope>(
            'POST',
            `admin-management/notifications`,
            axiosOrgInstance,
            body,
        );

        return response.data.success;
    }

    async function getAllNotificationTemplates() {
        const response = await makeRequest<ApiEnvelope<NotificationTemplate[]>>(
            'GET',
            `admin-management/notifications/templates`,
            axiosOrgInstance,
        );

        return response.data.data;
    }

    async function getLocationByNotificationId(id: string) {
        const isAdmin = localStorage.getItem('admin') === 'true';

        const response = isAdmin
            ? await makeRequest<ApiEnvelope<UUIDLocation[]>>(
                  'GET',
                  `admin-management/notifications/${id}/locations`,
                  axiosOrgInstance,
              )
            : await makeRequest<ApiEnvelope<UUIDLocation[]>>(
                  'GET',
                  `organization-management/organizations/${localStorage.getItem(
                      'organization_id',
                  )}/notifications/${id}/locations`,
                  axiosOrgInstance,
              );

        return sortLocationTree(response.data.data);
    }

    async function getSubscriptions() {
        const response = await makeRequest<ApiEnvelope<NotificationSubscriptionResponse[]>>(
            'GET',
            `organization-management/organizations/${localStorage.getItem(
                'organization_id',
            )}/notifications/subscriptions`,
            axiosOrgInstance,
        );

        return response.data.data;
    }

    async function putSubscriptions(body: SubscriptionFormBody[]) {
        const response = await makeRequest<ApiEnvelope>(
            'PUT',
            `organization-management/organizations/${localStorage.getItem(
                'organization_id',
            )}/notifications/subscriptions`,
            axiosOrgInstance,
            body,
        );

        return response.data.success;
    }

    async function acknowledgeNotification(id: string) {
        const response = await makeRequest(
            'PUT',
            `organization-management/organizations/${localStorage.getItem('organization_id')}/notifications/${id}/ack`,
            axiosOrgInstance,
        );

        setup();

        return response;
    }

    // move to Infra
    async function getOrganizationsByLocationId(locationId: string | string[]) {
        if (typeof locationId === 'string') locationId = [locationId];

        const orgs = new Set<string>();
        const data = await Promise.all(
            locationId.map(async (id) => {
                const response = await makeRequest<ApiEnvelope<OrganizationByLocation[]>>(
                    'GET',
                    `infrastructure-management/locations/${id}/organizations`,
                    axiosInfraInstance,
                );
                return response.data.data;
            }),
        ).then((res) => {
            // stringify so we can use Set to remove duplicates
            res.forEach((org) => org.forEach((o) => orgs.add(JSON.stringify(o))));
            return Array.from(orgs).map((o) => JSON.parse(o));
        });

        return data;
    }

    async function getNotificationThread(id: string) {
        const response = await makeRequest<ApiEnvelope<NotificationThreadResponseById[]>>(
            'GET',
            `organization-management/organizations/${localStorage.getItem(
                'organization_id',
            )}/notifications/${id}/thread`,
            axiosOrgInstance,
        );

        return response.data.data;
    }

    async function getAdminThread(id: string) {
        const response = await makeRequest<ApiEnvelope<NotificationThreadResponseById[]>>(
            'GET',
            `admin-management/notifications/${id}/thread`,
            axiosOrgInstance,
        );

        return response.data.data;
    }

    return {
        subscriptions,
        orgNotifications,
        filteredNotificationOrganizations,
        toastMessage,
        hasSubscribed,
        hasNotificationAccess,

        setup,
        setToastMessage,
        clearToastMessage,
        getNotificationTypes,
        getAllNotifications,
        getAdminNotification,
        getNotificationById,
        getNotifications,
        createNotification,
        getAllNotificationTemplates,
        getLocationByNotificationId,
        getSubscriptions,
        putSubscriptions,
        acknowledgeNotification,
        getOrganizationsByLocationId,
        getNotificationThread,
        getAdminThread,
    };
});
